import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import { useCallback, useMemo, useState } from "react";
import type { MRT_ColumnDef } from "material-react-table";
import { usePermission } from "src/helpers/Auth";
import { useIsAdmin } from "src/helpers/useIsAdmin";
import ShareDialog from "src/reusable/components/ShareDialog";
import { NucToast } from "src/reusable/components/Toast";
import NuclearnDataTable from "src/reusable/NuclearnDataTable/NuclearnDataTable";
import PersonaSelectorModal from "src/reusable/Chat/PromptInput/PersonaSelectorModal";
import PersonaDialog from "src/views/assistant/components/persona-list/PersonaDialog";
import type { PersonaAPIData } from "src/views/assistant/types";
import {
  fetchAgentConfig,
  useBindSlotPersona,
  useUnbindSlotPersona,
  useWorkflowProductDeclaration,
  type WorkflowPersonaSlot,
} from "../services/WorkflowProductService";
import type { WorkflowProductDescriptor } from "../types";

const SOURCE_LABELS: Record<string, string> = {
  binding: "bound by admin",
  resource_slot: "via resource slot",
  default: "seeded default",
};

const MODEL_SOURCE_LABELS: Record<string, string> = {
  persona_override: "agent override",
  persona_model_version: "pinned on agent",
  contract_default: "role default",
  platform_default: "platform default (general_llm)",
};

/**
 * Admin panel binding personas to a workflow product's slots — a gear beside
 * the product's other page-top controls. One row per declared slot showing
 * the currently resolved persona and which layer decided it; Change binds at
 * the global layer, Reset removes the binding so resolution falls back
 * (resource slot, then the seeded default). Only rendered for an admin.
 */
/** The API's error detail, or a fallback. Avoids `any` on the catch clauses. */
const apiErrorMessage = (error: unknown, fallback: string): string => {
  const detail = (error as { response?: { data?: { detail?: unknown } } })
    ?.response?.data?.detail;
  return typeof detail === "string" && detail ? detail : fallback;
};

const WorkflowPersonaSlotsPanel = ({
  product,
}: {
  product: WorkflowProductDescriptor;
}) => {
  const isAdmin = useIsAdmin();
  const [isOpen, setIsOpen] = useState(false);
  const [pickingSlotKey, setPickingSlotKey] = useState<string | null>(null);
  // Customize = copy-on-write: prefill the agent editor with the resolved
  // agent's full config (workers are hidden from listings, so this is the
  // only surface where their prompt is reachable), create a visible standard
  // copy, then bind it to the slot. The seeded agent stays pristine — it is
  // the vendor baseline reseeds upsert and Reset falls back to.
  const [customizing, setCustomizing] = useState<{
    slotKey: string;
    template: PersonaAPIData;
  } | null>(null);
  // A customized copy whose staged grants did not all land. The editor
  // unmounts on create, so the retry dialog lives here.
  const [agentForAccess, setAgentForAccess] = useState<PersonaAPIData | null>(
    null,
  );
  const canPublishAgents = usePermission("agent:promote").canIt;

  const { data: declaration } = useWorkflowProductDeclaration(
    product.productKey,
  );
  const { mutateAsync: bindPersona, isLoading: isBindingPersona } =
    useBindSlotPersona(product.productKey);
  const { mutateAsync: unbindPersona, isLoading: isUnbindingPersona } =
    useUnbindSlotPersona(product.productKey);
  // One in-flight guard across both mutations: two rapid picks for the
  // same slot would otherwise race with no defined winner.
  const isMutating = isBindingPersona || isUnbindingPersona;

  // Handlers live ABOVE the columns memo and the admin early-return: the
  // memo runs on the first render (declaration still loading), and cell
  // closures created then must capture initialized bindings — declaring
  // these after the early return left them in the temporal dead zone and
  // every action button crashed for admins (repeated automated-review
  // finding).
  const handlePicked = async (persona: PersonaAPIData | null) => {
    if (!pickingSlotKey) return;
    const slotKey = pickingSlotKey;
    setPickingSlotKey(null);
    if (!persona) return;
    try {
      await bindPersona({ slotKey, personaId: persona.id });
      NucToast({
        message: `Bound ${persona.name} to the ${slotKey} slot`,
        type: "success",
      });
    } catch (error: unknown) {
      NucToast({
        message: apiErrorMessage(error, "Failed to bind agent"),
        type: "error",
      });
    }
  };

  const handleCustomize = useCallback(
    async (slot: WorkflowPersonaSlot) => {
      if (!slot.resolved_persona) return;
      try {
        const config = await fetchAgentConfig(slot.resolved_persona.id);
        setCustomizing({
          slotKey: slot.slot_key,
          template: {
            ...config,
            // Empty slug = create, not edit; the copy is born a normal visible
            // agent (create payload carries no worker kind / slot-driven flag).
            // seeded:false matters — the editor locks prompt fields on seeded
            // agents ("system agent" banner), and the fetched worker config
            // carries seeded:true, which must not leak onto the editable copy.
            slug: "",
            name: `${config.name} (${product.displayName} custom)`,
            is_slot_driven: false,
            seeded: false,
          } as PersonaAPIData,
        });
      } catch (error: unknown) {
        NucToast({
          message: apiErrorMessage(error, "Failed to load the agent's config"),
          type: "error",
        });
      }
    },
    [product.displayName],
  );

  const handleCustomizedCreated = async (created: PersonaAPIData) => {
    if (!customizing) return;
    const slotKey = customizing.slotKey;
    setCustomizing(null);
    try {
      await bindPersona({ slotKey, personaId: created.id });
      NucToast({
        message: `Created ${created.name} and bound it to the ${slotKey} slot`,
        type: "success",
      });
    } catch (error: unknown) {
      NucToast({
        message: apiErrorMessage(
          error,
          `Created ${created.name} but binding failed — bind it via Change`,
        ),
        type: "error",
      });
    }
  };

  const handleReset = useCallback(
    async (slotKey: string) => {
      try {
        await unbindPersona({ slotKey });
        NucToast({ message: `Reset the ${slotKey} slot`, type: "success" });
      } catch (error: unknown) {
        NucToast({
          message: apiErrorMessage(error, "Failed to reset binding"),
          type: "error",
        });
      }
    },
    [unbindPersona],
  );

  // Concern-grouped columns (role / who / model), each cell carrying its own
  // value, provenance chip, and actions — the 5-column raw-MUI layout split
  // value and provenance from their actions and read cramped in the dialog.
  // NuclearnDataTable is the platform's shared table (MRT chrome, stable
  // per-column testids), the same surface the dataset viewers use.
  const columns = useMemo<MRT_ColumnDef<WorkflowPersonaSlot>[]>(
    () => [
      {
        id: "role",
        header: "Role",
        accessorFn: (slot) => slot.slot_key,
        Cell: ({ row }) => (
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {row.original.slot_key}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row.original.description}
            </Typography>
          </Box>
        ),
      },
      {
        id: "persona",
        header: "Agent",
        accessorFn: (slot) => slot.resolved_persona?.name ?? "",
        Cell: ({ row }) => {
          const slot = row.original;
          return (
            <Box>
              {slot.resolved_persona?.name ? (
                <Typography variant="body2">
                  {slot.resolved_persona.name}
                </Typography>
              ) : (
                <Typography variant="body2" color="error">
                  unresolved
                </Typography>
              )}
              {slot.resolution_source && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={
                    SOURCE_LABELS[slot.resolution_source] ??
                    slot.resolution_source
                  }
                  sx={{ mt: 0.5 }}
                />
              )}
              <Stack direction="row" spacing={0.5} mt={0.5}>
                <Button
                  size="small"
                  disabled={isMutating}
                  onClick={() => setPickingSlotKey(slot.slot_key)}
                  data-testid={`workflow-slot-change-${slot.slot_key}`}
                >
                  Change
                </Button>
                <Button
                  size="small"
                  disabled={isMutating || slot.resolution_source !== "binding"}
                  onClick={() => handleReset(slot.slot_key)}
                  data-testid={`workflow-slot-reset-${slot.slot_key}`}
                >
                  Reset
                </Button>
                <Button
                  size="small"
                  disabled={!slot.resolved_persona}
                  onClick={() => handleCustomize(slot)}
                  data-testid={`workflow-slot-customize-${slot.slot_key}`}
                >
                  Customize
                </Button>
              </Stack>
            </Box>
          );
        },
      },
      {
        id: "model",
        header: "Model",
        accessorFn: (slot) => slot.resolved_model?.name ?? "",
        Cell: ({ row }) => {
          const slot = row.original;
          return (
            <Box>
              {slot.resolved_model ? (
                <>
                  <Typography variant="body2">
                    {slot.resolved_model.name}
                  </Typography>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={
                      MODEL_SOURCE_LABELS[slot.resolved_model.source] ??
                      slot.resolved_model.source
                    }
                    sx={{ mt: 0.5 }}
                  />
                </>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  —
                </Typography>
              )}
            </Box>
          );
        },
      },
    ],
    [handleCustomize, handleReset],
  );

  // The declaration carries the slot rows, so there is nothing to show until
  // it arrives.
  if (!isAdmin || !declaration) {
    return null;
  }

  return (
    <>
      <Tooltip title={`Configure ${product.displayName} agents`}>
        <IconButton
          onClick={() => setIsOpen(true)}
          size="small"
          data-testid={`${product.testIdPrefix}-btn-persona-slots`}
        >
          <GroupsOutlinedIcon />
        </IconButton>
      </Tooltip>

      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{product.displayName} agents</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Each slot names a role in the workflow. Binding replaces WHO fills
            the role for everyone; the procedure the role follows does not
            change.
          </Typography>
          <NuclearnDataTable<WorkflowPersonaSlot>
            tableTestId="workflow-persona-slots"
            columns={columns}
            data={declaration.persona_slots}
            getRowId={(slot) => slot.slot_key}
            // A fixed handful of config rows in workflow order: sorting would
            // only scramble the pipeline reading, and resize/pagination/toolbar
            // chrome has nothing to offer at this size.
            enableSorting={false}
            enableColumnResizing={false}
            enablePagination={false}
            enableTopToolbar={false}
            enableBottomToolbar={false}
            emptyMessage="This product declares no agent slots."
          />
          <Box mt={2}>
            <Typography variant="caption" color="text.secondary">
              Resolution order: admin binding, then the legacy resource-slot
              binding, then the seeded default.
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      {customizing && (
        <PersonaDialog
          persona={customizing.template}
          open
          setOpen={(open) => {
            if (!open) setCustomizing(null);
          }}
          navigateOnCreate={false}
          onCreateSuccess={handleCustomizedCreated}
          onShareGrantsFailed={setAgentForAccess}
        />
      )}

      {agentForAccess && (
        <ShareDialog
          open
          onClose={() => setAgentForAccess(null)}
          resource={{
            type: "agent",
            id: agentForAccess.id,
            name: agentForAccess.name,
          }}
          canTransferOwnership
          canPublish={canPublishAgents}
        />
      )}

      <PersonaSelectorModal
        open={pickingSlotKey !== null}
        onClose={() => setPickingSlotKey(null)}
        onConfirm={handlePicked}
        // Required prop. Nothing is pre-highlighted: the modal is used here to
        // pick a NEW binding, and this panel already lists each slot's current
        // resolved persona beside it. The declaration's WorkflowSlotPersona
        // (id/slug/name/description) is not a PersonaAPIData, so it cannot be
        // passed through without a cast that would lie about the shape.
        selectedPersona={null}
      />
    </>
  );
};

export default WorkflowPersonaSlotsPanel;
